import Language from 'types/LanguageEnum';
import { getUrlParam } from 'utils/urlUtils';
import { ChineseDictionary } from 'components/Keyboard/Custom/Chinese/types';
import { DICTIONARY_BASE_PATH, DICTIONARY_PATHS } from 'constants/DictionaryConstants';
import { DictionaryLanguage, DictionaryName } from 'types/Dictionary';
import { MayBe } from 'types/common';

type Dictionary = ChineseDictionary;

const fetchDictionary = async <T extends Dictionary>(url: string, label: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${label}: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
};

const getDictionaryUrl = (path: string): string => {
  const token = getUrlParam('token') ?? getUrlParam('OSToken');
  const baseUrl = process.env.ASSETS_URL;
  return `${baseUrl}${DICTIONARY_BASE_PATH}/${path}?t=${token}`;
};

const loadDictionary = async <T extends Dictionary>(dictionaryName: DictionaryName): Promise<void> => {
  const existing = Dictionaries.get(dictionaryName) as T | undefined;

  if (!existing) {
    const path = DICTIONARY_PATHS[dictionaryName];
    const dictionary = await fetchDictionary<T>(getDictionaryUrl(path), dictionaryName);
    Dictionaries.set(dictionaryName, dictionary);
  }
};

const loadZhCnDictionary = () => loadDictionary<ChineseDictionary>(DictionaryName.PINYIN_SIMPLIFIED);

const loadTraditionalDictionaries = async () => {
  await Promise.all([
    loadDictionary<ChineseDictionary>(DictionaryName.PINYIN_TRADITIONAL),
    loadDictionary<ChineseDictionary>(DictionaryName.CANGJIE_TRADITIONAL),
  ]);
};

export const DICTIONARY_LOADERS: Record<DictionaryLanguage, () => Promise<void>> = {
  [Language.ZH_CN]: loadZhCnDictionary,
  [Language.ZH_TW]: loadTraditionalDictionaries,
  [Language.ZH_HK]: loadTraditionalDictionaries,
};

export class Dictionaries {
  private static cache: Partial<Record<DictionaryName, Dictionary>> = {};

  static get(dictionaryName: DictionaryName): MayBe<Dictionary> {
    return this.cache[dictionaryName];
  }

  static set(dictionaryName: DictionaryName, dictionary: Dictionary): void {
    this.cache[dictionaryName] = dictionary;
  }
}
